import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';
import { getEffectiveIsGlobal } from '../utils/adminViewMode';

const DashboardContext = createContext({
  loading: true,
  isGlobalUser: false,
  effectiveIsGlobal: false,
  isAdmin: false,
});

export function useDashboard() {
  return useContext(DashboardContext);
}

export function DashboardProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [isGlobalUser, setIsGlobalUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          if (mounted) {
            setIsAdmin(false);
            setIsGlobalUser(false);
            setLoading(false);
          }
          return;
        }
        const { data: globalRow } = await supabase
          .from('lean_admin_users')
          .select('user_id, is_global')
          .eq('user_id', userId)
          .eq('is_global', true)
          .maybeSingle();
        let allowed = !!globalRow;
        if (!allowed) {
          const [{ data: cli }, { data: reg }] = await Promise.all([
            supabase.from('admin_user_clients').select('user_id').eq('user_id', userId).limit(1),
            supabase.from('admin_user_regions').select('user_id').eq('user_id', userId).limit(1),
          ]);
          allowed = (cli && cli.length > 0) || (reg && reg.length > 0);
        }
        if (!mounted) return;
        setIsGlobalUser(!!globalRow);
        setIsAdmin(allowed);
      } catch {
        if (mounted) {
          setIsAdmin(false);
          setIsGlobalUser(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const { data: listener } = supabase.auth.onAuthStateChange(() => { load(); });
    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const effectiveIsGlobal = getEffectiveIsGlobal(isGlobalUser);

  return (
    <DashboardContext.Provider value={{ loading, isGlobalUser, effectiveIsGlobal, isAdmin }}>
      {children}
    </DashboardContext.Provider>
  );
}
